import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChapterProgress } from '../types';
import { Chapter } from '../types/content';
import ChapterContent from './ChapterContent';
import ChapterQuiz from './ChapterQuiz';

export interface MultiPageChapterReaderProps {
  chapter: Chapter;
  progress: ChapterProgress;
  onProgressUpdate: (progress: ChapterProgress) => void;
  onBackToList: () => void;
}

type ReaderSection = 'content' | 'quiz';

const MultiPageChapterReader: React.FC<MultiPageChapterReaderProps> = ({
  chapter,
  progress,
  onProgressUpdate,
  onBackToList
}) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentSection, setCurrentSection] = useState<ReaderSection>('content');
  const [pageCompletionStatus, setPageCompletionStatus] = useState<boolean[]>([]);
  const [pageQuizStatus, setPageQuizStatus] = useState<boolean[]>([]);
  const [showPageNav, setShowPageNav] = useState(false);

  const [isTransitioning, setIsTransitioning] = useState(false);

  const pages = useMemo(() => chapter.pages || [], [chapter.pages]);
  const currentPage = pages[currentPageIndex];

  // Initialize completion status
  useEffect(() => {
    const currentReadingProgress = progress?.readingProgress || 0;
    const completedQuizzesCount = Math.round((currentReadingProgress / 100) * pages.length);

    const initialPageCompletion = pages.map(() => false);
    const initialQuizCompletion = pages.map((_, index) => index < completedQuizzesCount);

    setPageCompletionStatus(initialPageCompletion);
    setPageQuizStatus(initialQuizCompletion);
  }, [pages, pages.length, progress?.readingProgress, progress?.isCompleted, chapter.id]);

  const handlePageContentComplete = () => {
    const updatedStatus = [...pageCompletionStatus];
    updatedStatus[currentPageIndex] = true;
    setPageCompletionStatus(updatedStatus);

    // Calculate new reading progress
    const completedPagesCount = updatedStatus.filter(Boolean).length;
    const newReadingProgress = Math.round((completedPagesCount / pages.length) * 100);
    const isNowCompleted = newReadingProgress === 100;

    // Check if chapter has any quizzes
    const hasQuizzes = pages.some(p => p.questions && p.questions.length > 0);

    // If we reach 100% reading AND there are no quizzes, mark as completed
    // If there ARE quizzes, we wait for quiz completion
    const shouldMarkCompleted = isNowCompleted && !hasQuizzes;

    // Update parent component to save progress
    onProgressUpdate({
      ...progress,
      chapterId: chapter.id,
      readingProgress: newReadingProgress,
      isCompleted: shouldMarkCompleted || progress.isCompleted,
      completedAt: (shouldMarkCompleted && !progress.isCompleted) ? new Date() : progress.completedAt
    });
  };

  const [pageProgress, setPageProgress] = useState(0);
  const [hasReachedQuizThreshold, setHasReachedQuizThreshold] = useState(false);

  useEffect(() => {
    setPageProgress(0);
    setHasReachedQuizThreshold(false);
  }, [currentPageIndex]);

  const handlePageContentProgressUpdate = (progress: number) => {
    setPageProgress(progress);
    // Once user reads 70%+, latch the quiz button visible — stays even if they scroll back
    if (progress >= 70) {
      setHasReachedQuizThreshold(true);
    }
  };

  const handlePageQuizComplete = (score: number, totalQuestions: number) => {
    const updatedQuizStatus = [...pageQuizStatus];
    updatedQuizStatus[currentPageIndex] = true;
    setPageQuizStatus(updatedQuizStatus);

    const completedQuizzes = updatedQuizStatus.filter(Boolean).length;
    const completedPages = pageCompletionStatus.filter(Boolean).length;

    console.log('[MultiPageChapterReader] Quiz Complete Debug:', {
      currentPageIndex,
      totalPages: pages.length,
      updatedQuizStatus,
      completedQuizzes,
      pagesWithQuestions: pages.map((p, i) => ({ i, hasQ: p.questions && p.questions.length > 0 }))
    });

    const readingProgressFromQuizzes = Math.round((completedQuizzes / pages.length) * 100);
    const readingProgressFromContent = Math.round((completedPages / pages.length) * 100);

    // Take the better of the two to prevent regression
    const newReadingProgress = Math.max(progress?.readingProgress || 0, readingProgressFromContent, readingProgressFromQuizzes);

    // Check completion: All pages that have questions must be completed
    // User Request Update: "complete upon finishing the last quiz"
    // We will now check if this is the LAST page that has a quiz. If so, and we just finished it, we mark as complete.

    // Find the index of the last page that has questions
    let lastQuizPageIndex = -1;
    for (let i = pages.length - 1; i >= 0; i--) {
      if (pages[i].questions && pages[i].questions.length > 0) {
        lastQuizPageIndex = i;
        break;
      }
    }

    const areAllQuizzesPassed = pages.every((page, index) => {
      const hasQuestions = page.questions && page.questions.length > 0;
      if (!hasQuestions) return true; // No quiz needed for this page
      return updatedQuizStatus[index]; // Check if done
    });

    const isLastQuizPage = currentPageIndex === lastQuizPageIndex;
    // Mark as complete if ALL are passed OR if we just finished the LAST quiz
    const isNowCompleted = areAllQuizzesPassed || isLastQuizPage;

    console.log('[MultiPageChapterReader] Completion Check:', {
      areAllQuizzesPassed,
      isLastQuizPage,
      lastQuizPageIndex,
      newReadingProgress,
      previousCompletion: progress?.isCompleted
    });

    const currentPageScore = Math.round((score / totalQuestions) * 100);

    const updatedProgress: ChapterProgress = {
      ...progress, // Preserve existing fields
      chapterId: chapter.id,
      isCompleted: isNowCompleted || progress?.isCompleted,
      quizScore: currentPageScore, // Note: This overwrites quizScore with current page score. Is this intended?
      // Original logic seemed to overwrite it. Probably should average it or keep best?
      // But keeping it simple for now as per original intent (likely just last quiz score or simplified model)
      // Actually original logic just set it to currentPageScore. Let's keep it consistent.
      completedAt: (isNowCompleted || progress?.isCompleted) ? (progress?.completedAt || new Date()) : undefined,
      readingProgress: newReadingProgress
    };

    console.log('[MultiPageChapterReader] Sending Progress Update:', updatedProgress);

    onProgressUpdate(updatedProgress);
    // Auto-advance removed as per user request
  };

  const handleNavigateToQuiz = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSection('quiz');
      setIsTransitioning(false);
    }, 300);
  };

  const handleBackToContent = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSection('content');
      setIsTransitioning(false);
    }, 300);
  };

  const handleNextPage = useCallback(() => {
    if (currentPageIndex < pages.length - 1) {
      setCurrentPageIndex(currentPageIndex + 1);
      setCurrentSection('content');
    }
  }, [currentPageIndex, pages.length]);

  const handlePreviousPage = useCallback(() => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
      setCurrentSection('content');
    }
  }, [currentPageIndex]);

  const handlePageSelect = useCallback((pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    setCurrentSection('content');
    setShowPageNav(false);
  }, []);

  const isCurrentPageContentCompleted = pageCompletionStatus[currentPageIndex] || false;
  const isCurrentPageQuizCompleted = pageQuizStatus[currentPageIndex] || false;

  if (!currentPage) {
    return (
      <div className="h-full flex items-center justify-center bg-background dark:bg-[#142328]">
        <p className="text-gray-500 dark:text-gray-400">Kapitola nenalezena</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background dark:bg-[#142328]" role="main">
      {/* Top Toolbar Header Container (Desktop/Tablet) */}
      <header className="hidden sm:block flex-none px-6 py-3 z-50 mx-4 mt-4 mb-2 relative">

        {/* Background Layer with Masking & Styles */}
        <div className="absolute inset-0 bg-white/80 dark:bg-[#1b2e35]/80 backdrop-blur-md border border-gray-200 dark:border-gray-700/50 rounded-full overflow-hidden">
          {/* Progress Bar */}
          <div className="absolute bottom-0 left-0 h-1 bg-gray-100 dark:bg-gray-700 w-full opacity-50"></div>
          <div
            className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500 ease-out"
            style={{ width: `${pageProgress}%` }}
          />
        </div>

        {/* Content Layer - No Overflow Hidden (allows dropdowns) */}
        <div className="w-full h-full flex items-center justify-between gap-4 relative z-10">

          {/* Left: Back Button & Title */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <button
              onClick={onBackToList}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white 
                       bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10
                       border border-transparent hover:border-gray-300 dark:hover:border-gray-600
                       rounded-full px-3 py-2 transition-all duration-200"
              aria-label="Vrátit se na seznam kapitol"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium hidden sm:inline">Zpět</span>
            </button>

            <div className="flex flex-col min-w-0">
              <h2 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate leading-tight">
                {chapter.title}
              </h2>
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate hidden sm:block">
                {currentSection === 'content' ? `Stránka ${currentPageIndex + 1} z ${pages.length}` : 'Kvíz'}
              </span>
            </div>
          </div>

          {/* Right: Controls (Page Nav & Toggle) */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Page Navigation Dropdown Trigger */}
            <div className="relative">
              <button
                onClick={() => setShowPageNav(!showPageNav)}
                className="flex items-center gap-2 text-sm font-medium
                           text-gray-700 dark:text-gray-200
                           bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10
                           border border-transparent hover:border-gray-300 dark:hover:border-gray-600
                           rounded-full px-3 py-2 transition-all duration-200"
              >
                <span className="hidden sm:inline">Stránka</span>
                <span className="bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 px-1.5 py-0.5 rounded text-xs">
                  {currentPageIndex + 1}/{pages.length}
                </span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${showPageNav ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showPageNav && (
                <div className="absolute top-full right-0 mt-2 z-[60]
                                bg-white dark:bg-[#1b2e35] 
                                border border-gray-200 dark:border-gray-700/50 
                                rounded-xl shadow-xl ring-1 ring-black/5
                                p-2 min-w-[240px] max-h-[60vh] overflow-y-auto
                                animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 px-2 uppercase tracking-wider">
                    Obsah kapitoly
                  </div>
                  <div className="space-y-1">
                    {pages.map((page, index) => (
                      <button
                        key={index}
                        onClick={() => handlePageSelect(index)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-full text-sm transition-all duration-200 group text-left ${index === currentPageIndex
                          ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 ring-1 ring-orange-200 dark:ring-orange-500/20'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
                          }`}
                      >
                        <span className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${index === currentPageIndex
                          ? 'bg-orange-500 text-white'
                          : pageQuizStatus[index]
                            ? 'bg-green-500 text-white'
                            : pageCompletionStatus[index]
                              ? 'bg-yellow-500 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600'
                          }`}>
                          {index + 1}
                        </span>
                        <span className="truncate flex-1 font-medium">{page.title}</span>
                        {pageQuizStatus[index] && (
                          <svg className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quiz/Content Toggle */}
            {currentSection === 'content' && isCurrentPageContentCompleted ? (
              <button
                onClick={handleNavigateToQuiz}
                className="flex items-center gap-2
                           bg-gradient-to-r from-orange-500 to-orange-600 text-white
                           px-4 py-2 rounded-lg shadow-md shadow-orange-500/20
                           hover:shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5
                           transition-all duration-200 font-medium text-sm"
              >
                <span className="hidden sm:inline">Kvíz</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </button>
            ) : currentSection === 'quiz' ? (
              <button
                onClick={handleBackToContent}
                className="flex items-center gap-2
                           bg-white dark:bg-white/10 text-gray-700 dark:text-white
                           border border-gray-200 dark:border-gray-600
                           px-4 py-2 rounded-lg shadow-sm
                           hover:bg-gray-50 dark:hover:bg-white/20
                           transition-all duration-200 font-medium text-sm"
              >
                <span className="hidden sm:inline">Obsah</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </button>
            ) : null}
          </div>
        </div>
      </header>



      {/* Content Area */}
      <div className={`flex-1 overflow-hidden transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {currentSection === 'content' ? (
          <div className="h-full relative"> {/* Mobile padding moved to ChapterContent */}
            <ChapterContent
              key={`page-${currentPageIndex}`}
              content={currentPage.content}
              title={currentPage.title}
              onContentComplete={handlePageContentComplete}
              onProgressUpdate={handlePageContentProgressUpdate}
            />

            {/* Navigation Buttons - Bottom (Desktop) */}
            <div className="hidden sm:flex fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 items-center gap-3">
              {/* Previous Page */}
              {currentPageIndex > 0 && (
                <button
                  onClick={handlePreviousPage}
                  className="bg-white/90 dark:bg-[#1b2e35]/80 backdrop-blur-sm 
                           text-gray-600 dark:text-gray-300 
                           border border-gray-200 dark:border-gray-700/50
                           px-5 py-3 rounded-full shadow-lg
                           hover:bg-white dark:hover:bg-[#1b2e35] 
                           hover:text-gray-900 dark:hover:text-white 
                           hover:border-gray-300 dark:hover:border-gray-600
                           focus:outline-none focus:ring-2 focus:ring-gray-500/50
                           transition-all duration-300 flex items-center gap-2"
                  aria-label="Předchozí stránka"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="hidden sm:inline text-sm font-medium">Předchozí</span>
                </button>
              )}

              {/* Quiz or Next Page */}
              {!isCurrentPageQuizCompleted ? (
                <button
                  onClick={handleNavigateToQuiz}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white
                           px-8 py-4 rounded-full shadow-2xl shadow-orange-500/40
                           hover:shadow-orange-500/60 hover:scale-105 hover:-translate-y-1
                           focus:outline-none focus:ring-2 focus:ring-orange-500/50
                           transition-all duration-300 flex items-center gap-3 font-medium
                           animate-bounce-subtle"
                  aria-label="Pokračovat na kvíz"
                >
                  <span>Pokračovat na kvíz</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                </button>
              ) : currentPageIndex < pages.length - 1 ? (
                <button
                  onClick={handleNextPage}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white
                           px-8 py-4 rounded-full shadow-2xl shadow-green-500/40
                           hover:shadow-green-500/60 hover:scale-105 hover:-translate-y-1
                           focus:outline-none focus:ring-2 focus:ring-green-500/50
                           transition-all duration-300 flex items-center gap-3 font-medium
                           animate-bounce-subtle"
                  aria-label="Další stránka"
                >
                  <span>Další stránka</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-4 sm:p-6 pb-32">
            <div className="max-w-4xl mx-auto">
              <ChapterQuiz
                questions={currentPage.questions}
                onQuizComplete={handlePageQuizComplete}
                onBackToContent={handleBackToContent}
                nextPageTitle={currentPageIndex < pages.length - 1 ? pages[currentPageIndex + 1]?.title : undefined}
                onContinueNow={handleNextPage}
                onQuizRestart={() => {
                  // No specific restart logic needed here beyond resetting quiz state which component does
                  // But we might want to track restarts
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Mobile Footer (Fixed Bottom) */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-50
                    bg-white/95 dark:bg-[#1b2e35]/95 backdrop-blur-md
                    border-t border-gray-200 dark:border-gray-700/50
                    pb-safe"> {/* pb-safe for iPhone Home Indicator */}

        {/* Progress Bar (Top of Footer) */}
        <div className="absolute top-0 left-0 h-1 bg-gray-100 dark:bg-gray-700 w-full opacity-50"></div>
        <div
          className="absolute top-0 left-0 h-1 bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500 ease-out"
          style={{ width: `${pageProgress}%` }}
        />

        <div className="flex items-center justify-between px-4 py-3">
          {/* Back & Page Info (Left) */}
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToList}
              className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex flex-col">
              <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider leading-none">Stránka</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white leading-none mt-0.5">
                {currentPageIndex + 1} <span className="text-gray-400 font-normal">/ {pages.length}</span>
              </span>
            </div>
          </div>

          {/* Action Button (Right) */}
          <div>
            {currentSection === 'content' ? (
              (isCurrentPageContentCompleted || hasReachedQuizThreshold) ? (
                <button
                  onClick={handleNavigateToQuiz}
                  className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg shadow-orange-500/30"
                >
                  Kvíz
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <div className="flex gap-2">
                  {currentPageIndex > 0 && (
                    <button
                      onClick={handlePreviousPage}
                      className="p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={handleNextPage}
                    className={`p-2 ${isCurrentPageQuizCompleted || isCurrentPageContentCompleted ? 'text-gray-900 dark:text-white' : 'text-gray-300 dark:text-gray-600'}`}
                    disabled={!isCurrentPageQuizCompleted && !isCurrentPageContentCompleted}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )
            ) : (
              <button
                onClick={handleBackToContent}
                className="flex items-center gap-2 bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white px-4 py-2 rounded-full text-sm font-medium"
              >
                Zpět k textu
              </button>
            )}
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MultiPageChapterReader;